import { isSubtreeCompleteDown, isSubtreeCompleteUp } from "./branchProjection";
import type { PaginatedNode } from "./PaginatedNode";
import type { BranchProjectionCtx, ID, LoadRequest, PageFetcher, PageRequestKey } from "./types";
import {
  findChildLoadedBoundaries,
  getChildSlotAt,
  isEmptyAwaitingFromLast,
  locateChildInParent,
} from "./pagination.utils";

/**
 * Per-direction flow signals used by `walkNode`. Direction is the outer
 * struct, so each direction's `canLoad` and `nearEdge` read symmetrically.
 *
 *   - `canLoad` — the PagedList can still fetch another page in this
 *                 direction (`pages.canLoad(direction) === true`).
 *   - `nearEdge` — visible ids intersect the last/first `edgeThreshold`
 *                  records of the corresponding boundary page.
 */
interface DirectionFlow {
  readonly canLoad: boolean;
  readonly nearEdge: boolean;
}

/**
 * Raw flow signals used by `walkNode` and its sibling gate. Kept as a
 * small immutable record so every decision reads as a named combination
 * of flags instead of inline `isNearBoundary` / `canLoad` calls.
 */
interface NodeFlowState {
  readonly hasNoLoadedItems: boolean;
  readonly after: DirectionFlow;
  readonly before: DirectionFlow;
}

/** Loaded-zone boundaries returned by `findChildLoadedBoundaries`. */
interface LoadedBoundaries {
  readonly firstLoadedIdx: number;
  readonly lastLoadedIdx: number;
}

/** Anchor (loaded sibling) and boundary (empty slot just past it). */
interface AnchorBoundaryPair {
  readonly anchor: { readonly id: ID; readonly child: PaginatedNode };
  readonly boundary: { readonly id: ID; readonly child: PaginatedNode };
}

/**
 * Inputs to a single `planLoad` call. The planner is a pure function of
 * these inputs — no I/O, no tree mutation, no internal state.
 */
export interface LoadPlannerArgs {
  readonly root: PaginatedNode;
  readonly visibleIds: ReadonlySet<ID>;
  readonly collapsedIds: ReadonlySet<ID>;
  readonly cleanedCollapsed: ReadonlySet<ID>;
  readonly edgeThreshold: number;
  readonly getFetcher: (depth: number) => PageFetcher;
  readonly hasNextLevel: (depth: number) => boolean;
  readonly branchProjectionCtx: BranchProjectionCtx;
}

interface MakeLoadRequestArgs {
  readonly ctx: LoadPlannerArgs;
  readonly node: PaginatedNode;
  readonly direction: "after" | "before";
  readonly key: PageRequestKey;
  readonly reload?: boolean;
}

interface TryFromLastArgs {
  readonly ctx: LoadPlannerArgs;
  readonly node: PaginatedNode;
  readonly parent: PaginatedNode;
  readonly flow: NodeFlowState;
}

interface TryProgressBoundaryArgs {
  readonly ctx: LoadPlannerArgs;
  readonly node: PaginatedNode;
  readonly direction: "after" | "before";
  readonly boundaries: LoadedBoundaries;
}

interface EvaluateChildForDescentArgs {
  readonly ctx: LoadPlannerArgs;
  readonly parent: PaginatedNode;
  readonly childId: ID;
  readonly absoluteIdx: number;
  readonly boundaries: LoadedBoundaries;
}

/**
 * Outcome of evaluating one child slot during step (1) descent.
 * Discriminated union replaces the old flag-then-break pattern.
 */
type DescentOutcome =
  | { readonly kind: "load"; readonly request: LoadRequest }
  | { readonly kind: "blocked" }
  | { readonly kind: "skip" };

/**
 * Decide at most ONE LoadRequest per cycle. Returns `null` when there
 * is nothing to load.
 *
 *   empty?         → bootstrap            (step 0)
 *   has children   → descend recursively  (step 1)
 *   near after     → forward load         (step 2)
 *   near before    → backward load        (step 2b)
 *   blocked sib    → fromLast probe       (step 2c)
 *   stale visible  → reload               (step 3)
 *   else           → null
 */
export function planLoad(args: LoadPlannerArgs): LoadRequest | null {
  return walkNode(args, args.root, null);
}

/**
 * Decides at most one `LoadRequest` for this node and its visible
 * subtree per call. Returns `null` when there is nothing to do.
 */
function walkNode(
  ctx: LoadPlannerArgs,
  node: PaginatedNode,
  parent: PaginatedNode | null,
): LoadRequest | null {
  if (node.pages.isEmpty) {
    return bootstrapEmptyNode(ctx, node);
  }

  if (ctx.hasNextLevel(node.depth)) {
    const descentLoad = descendIntoChildren(ctx, node);
    if (descentLoad) {
      return descentLoad;
    }
  }

  const flow = classifyNodeFlow(ctx, node);
  if (flow.hasNoLoadedItems) {
    return null;
  }

  const afterLoad = tryAfterLoad(ctx, node, flow);
  if (afterLoad) {
    return afterLoad;
  }

  const beforeLoad = tryBeforeLoad(ctx, node, flow);
  if (beforeLoad) {
    return beforeLoad;
  }

  if (parent !== null) {
    const fromLastLoad = tryFromLastOnEarlierSibling({ ctx, node, parent, flow });
    if (fromLastLoad) {
      return fromLastLoad;
    }
  }

  return tryStaleReload(ctx, node);
}

/** Step (0): fetch the first forward page for a node that has none. */
function bootstrapEmptyNode(ctx: LoadPlannerArgs, node: PaginatedNode): LoadRequest | null {
  if (!node.pages.canLoad("after")) {
    return null;
  }
  const key: PageRequestKey = { kind: "after", cursor: null };
  if (node.pages.hasPageWithCursor(key)) {
    return null;
  }
  return makeLoadRequest({ ctx, node, direction: "after", key });
}

/** Step (2): forward load when visible ids are within the after-end edge. */
function tryAfterLoad(
  ctx: LoadPlannerArgs,
  node: PaginatedNode,
  flow: NodeFlowState,
): LoadRequest | null {
  if (!flow.after.canLoad || !flow.after.nearEdge) {
    return null;
  }
  const key: PageRequestKey = { kind: "after", cursor: node.pages.afterCursor };
  if (node.pages.hasPageWithCursor(key)) {
    return null;
  }
  return makeLoadRequest({ ctx, node, direction: "after", key });
}

/** Step (2b): backward load when visible ids are within the before-end edge. */
function tryBeforeLoad(
  ctx: LoadPlannerArgs,
  node: PaginatedNode,
  flow: NodeFlowState,
): LoadRequest | null {
  if (!flow.before.canLoad || !flow.before.nearEdge) {
    return null;
  }
  const beforeCursor = node.pages.beforeCursor;
  if (beforeCursor === null) {
    return null;
  }
  const key: PageRequestKey = { kind: "before", cursor: beforeCursor };
  if (node.pages.hasPageWithCursor(key)) {
    return null;
  }
  return makeLoadRequest({ ctx, node, direction: "before", key });
}

/**
 * Step (2c): cross-sibling scroll-up probe. Fires only when this node is
 * before-exhausted yet the user is still near its before-edge — the
 * remaining work belongs to an earlier sibling.
 */
function tryFromLastOnEarlierSibling(args: TryFromLastArgs): LoadRequest | null {
  const { ctx, node, parent, flow } = args;
  if (!flow.before.nearEdge || flow.before.canLoad) {
    return null;
  }
  return findFromLastCandidate(ctx, node, parent);
}

/** Step (3): re-fetch the first stale page that intersects the viewport. */
function tryStaleReload(ctx: LoadPlannerArgs, node: PaginatedNode): LoadRequest | null {
  if (!node.stale) {
    return null;
  }
  for (const page of node.pages.pages) {
    if (
      node.pages.isPageStale(page.requestCursor) &&
      page.itemIds.some((id) => ctx.visibleIds.has(id))
    ) {
      return makeLoadRequest({
        ctx,
        node,
        direction: "after",
        key: page.requestCursor,
        reload: true,
      });
    }
  }
  return null;
}

/**
 * Step (1) descent. Walks visible non-collapsed children in document
 * order, recurses into each, and stops as soon as it gets a
 * `LoadRequest` or hits a sibling-gate block.
 *
 * Symmetric skip rules:
 *   - Empty visible child with a loaded sibling LATER in the same
 *     parent → skip descent. Step (0) on it would bootstrap
 *     `kind:"after"` (forward) which is wrong when the user is
 *     scrolling up; step (2c) on the loaded sibling will fire
 *     `fromLast` instead.
 *   - Empty visible child with a loaded sibling EARLIER in the same
 *     parent (and none later) → descend; step (0) bootstrap is the
 *     correct forward-scroll behaviour.
 *   - No loaded siblings at all (initial bootstrap) → descend; step
 *     (0) bootstraps the first slot.
 */
function descendIntoChildren(ctx: LoadPlannerArgs, node: PaginatedNode): LoadRequest | null {
  const boundaries = findChildLoadedBoundaries(node);
  let blocked = false;
  let load: LoadRequest | null = null;

  for (const slot of iterateChildSlots(node)) {
    const outcome = evaluateChildForDescent({
      ctx,
      parent: node,
      childId: slot.childId,
      absoluteIdx: slot.absoluteIdx,
      boundaries,
    });
    if (outcome.kind === "load") {
      load = outcome.request;
      break;
    }
    if (outcome.kind === "blocked") {
      blocked = true;
      break;
    }
  }

  // Chain progression: visibility-bound descent stalls when the next
  // boundary header sits just below the viewport. Eagerly bootstrap one
  // boundary per cycle (mirrors `fillForwardExposed`'s chainComplete rule).
  if (load === null && !blocked && boundaries.lastLoadedIdx >= 0) {
    const forwardEager = tryProgressBoundary({ ctx, node, direction: "after", boundaries });
    if (forwardEager) {
      return forwardEager;
    }
    return tryProgressBoundary({ ctx, node, direction: "before", boundaries });
  }

  return load;
}

/**
 * Walk every child slot (childId + absolute document-order index) in
 * a single flat sequence, decoupling the page-then-item nesting from
 * the per-slot decision in `descendIntoChildren`.
 */
function* iterateChildSlots(
  parent: PaginatedNode,
): Generator<{ readonly childId: ID; readonly absoluteIdx: number }> {
  let absoluteIdx = -1;
  for (const page of parent.pages.pages) {
    for (const childId of page.itemIds) {
      absoluteIdx++;
      yield { childId, absoluteIdx };
    }
  }
}

/**
 * Per-slot decision used by `descendIntoChildren`. The discriminated
 * `DescentOutcome` lets the caller use clean early-return control
 * flow instead of `let stop = false` / `let blocked = false` flags.
 */
function evaluateChildForDescent(args: EvaluateChildForDescentArgs): DescentOutcome {
  const { ctx, parent, childId, absoluteIdx, boundaries } = args;
  const childNode = parent.getChild(childId);
  if (!childNode) {
    return { kind: "skip" };
  }
  if (!ctx.visibleIds.has(childId) || ctx.collapsedIds.has(childId)) {
    return { kind: "skip" };
  }
  if (isEmptyAwaitingFromLast(childNode, absoluteIdx, boundaries)) {
    return { kind: "skip" };
  }

  const load = walkNode(ctx, childNode, parent);
  if (load) {
    return { kind: "load", request: load };
  }

  if (!canAdvancePastSibling(ctx, childNode)) {
    return { kind: "blocked" };
  }
  return { kind: "skip" };
}

/**
 * Bootstrap the immediate boundary past the loaded zone (forward or
 * backward) without requiring its id to be visible. Gated by the same
 * `chainComplete` invariant as `fillForwardExposed` / `fillBackwardExposed`:
 * the adjacent loaded sibling must be subtree-complete on the matching side.
 */
function tryProgressBoundary(args: TryProgressBoundaryArgs): LoadRequest | null {
  const { ctx, direction } = args;
  const pair = findAnchorBoundaryPair(args);
  if (!pair) {
    return null;
  }
  if (!isUserAtAnchorEdge(ctx, pair, direction)) {
    return null;
  }

  const subtreeComplete =
    direction === "after"
      ? isSubtreeCompleteDown(pair.anchor.child, ctx.branchProjectionCtx)
      : isSubtreeCompleteUp(pair.anchor.child, ctx.branchProjectionCtx);
  if (!subtreeComplete) {
    return null;
  }

  if (direction === "after") {
    return bootstrapEmptyNode(ctx, pair.boundary.child);
  }
  const key: PageRequestKey = { kind: "fromLast" };
  if (pair.boundary.child.pages.hasPageWithCursor(key)) {
    return null;
  }
  return makeLoadRequest({ ctx, node: pair.boundary.child, direction: "before", key });
}

/**
 * Resolve the (anchor, boundary) child pair adjacent to the loaded
 * zone for `tryProgressBoundary`. Returns `null` when the boundary
 * slot is missing, collapsed, or already-loaded — collapsing the
 * "early-out" branches that previously inflated `tryProgressBoundary`
 * past the complexity guardrail.
 */
function findAnchorBoundaryPair(args: TryProgressBoundaryArgs): AnchorBoundaryPair | null {
  const { ctx, node, direction, boundaries } = args;
  const anchorIdx = direction === "after" ? boundaries.lastLoadedIdx : boundaries.firstLoadedIdx;
  const boundaryIdx = direction === "after" ? anchorIdx + 1 : anchorIdx - 1;

  const boundary = getChildSlotAt(node, boundaryIdx);
  if (!boundary || ctx.collapsedIds.has(boundary.id)) {
    return null;
  }
  if (!boundary.child.pages.isEmpty || !boundary.child.pages.canLoad(direction)) {
    return null;
  }
  const anchor = getChildSlotAt(node, anchorIdx);
  if (!anchor) {
    return null;
  }
  return { anchor, boundary };
}

/**
 * True when the user's visible ids reach the anchor's matching edge —
 * either the trailing/leading records of the anchor itself, or the
 * boundary header (the latter unblocks scroll-up when the scrollbar
 * was dragged to the very top).
 */
function isUserAtAnchorEdge(
  ctx: LoadPlannerArgs,
  pair: AnchorBoundaryPair,
  direction: "after" | "before",
): boolean {
  const threshold = Math.max(1, ctx.edgeThreshold);
  const proximity = pair.anchor.child.pages.isNearBoundary(ctx.visibleIds, threshold);
  const userAtAnchorRecords = direction === "after" ? proximity.nearEnd : proximity.nearStart;
  return userAtAnchorRecords || ctx.visibleIds.has(pair.boundary.id);
}

/**
 * Classify a node's current state against the latest `visibleIds`.
 *
 * `nearEdge` is computed even when `canLoad` is false on both sides
 * — fully-loaded nodes still rely on `nearEdge` for two decisions:
 *   - `canAdvancePastSibling` uses `after.nearEdge` to gate forward
 *     iteration past a fully-consumed sibling.
 *   - `tryFromLastOnEarlierSibling` uses `before.nearEdge` (with
 *     `!before.canLoad`) to fire `fromLast` against an earlier
 *     sibling on scroll-up.
 */
function classifyNodeFlow(ctx: LoadPlannerArgs, node: PaginatedNode): NodeFlowState {
  const hasNoLoadedItems = node.pages.items.length === 0;
  const after: DirectionFlow = { canLoad: node.pages.canLoad("after"), nearEdge: false };
  const before: DirectionFlow = { canLoad: node.pages.canLoad("before"), nearEdge: false };
  if (hasNoLoadedItems) {
    return { hasNoLoadedItems, after, before };
  }
  const threshold = Math.max(1, ctx.edgeThreshold);
  const boundary = node.pages.isNearBoundary(ctx.visibleIds, threshold);
  return {
    hasNoLoadedItems,
    after: { canLoad: after.canLoad, nearEdge: boundary.nearEnd },
    before: { canLoad: before.canLoad, nearEdge: boundary.nearStart },
  };
}

/**
 * Sibling-gate for forward iteration in step (1). Allows walkNode to
 * advance past this child to the next visible sibling only when the
 * user has reached the end of a forward-exhausted node (or the child
 * has no loaded items at all).
 *
 * Backward iteration is handled separately by `findFromLastCandidate`
 * (step 2c), which has its own stop-at-first-empty rule, so step (1)
 * does not need a mirror "backward sibling-gate".
 */
function canAdvancePastSibling(ctx: LoadPlannerArgs, node: PaginatedNode): boolean {
  const flow = classifyNodeFlow(ctx, node);
  // Forward-exhausted ⇒ advance freely. Partially loaded ⇒ stay focused
  // here so the remaining pages load before the chain wanders off.
  return flow.hasNoLoadedItems || !flow.after.canLoad;
}

/**
 * Step (2c): look for an earlier sibling within the same parent that
 * needs a `fromLast` fetch to prime itself. Siblings are scanned in
 * reverse document order (first the siblings BEFORE `node` within the
 * current parent page, then earlier parent pages). Collapsed siblings
 * are skipped. The first candidate that already has pages short-circuits
 * the search with no fetch — the user will naturally scroll into its
 * after-end as they cross the boundary. The first candidate with NO
 * pages emits a `{ kind: "fromLast" }` request.
 *
 * If no candidate is found inside the current parent, returns null —
 * the parent's own step (2b) will eventually before-load the parent's
 * earlier page, which re-materializes fresh sibling nodes, and the
 * next cycle re-enters step (2c) with those in scope.
 */
function findFromLastCandidate(
  ctx: LoadPlannerArgs,
  node: PaginatedNode,
  parent: PaginatedNode,
): LoadRequest | null {
  const location = locateChildInParent(parent, node.id);
  if (!location) {
    return null;
  }

  // Walk backwards from (pageIdx, siblingIdx - 1) through earlier indices,
  // then through earlier parent pages.
  for (const siblingId of iterateEarlierSiblings(parent, location)) {
    const outcome = evaluateSiblingForFromLast(ctx, parent, siblingId);
    if (outcome.kind === "abort") {
      return null;
    }
    if (outcome.kind === "emit") {
      return outcome.request;
    }
  }

  return null;
}

/**
 * Yield sibling ids strictly before `location` in reverse document
 * order (right-to-left within the starting parent page, then earlier
 * parent pages). Decouples the per-sibling decision in
 * `findFromLastCandidate` from the page/sibling iteration.
 */
function* iterateEarlierSiblings(
  parent: PaginatedNode,
  location: { readonly pageIdx: number; readonly siblingIdx: number },
): Generator<ID> {
  const pages = parent.pages.pages;
  for (let pageIdx = location.pageIdx; pageIdx >= 0; pageIdx--) {
    const page = pages[pageIdx];
    const startIdx = pageIdx === location.pageIdx ? location.siblingIdx - 1 : page.items.length - 1;
    for (let siblingIdx = startIdx; siblingIdx >= 0; siblingIdx--) {
      yield page.itemIds[siblingIdx];
    }
  }
}

/**
 * Per-sibling outcome for `findFromLastCandidate`:
 *   - `skip`  — collapsed / missing / before-exhausted empty: keep scanning.
 *   - `abort` — sibling already has pages: walkNode will drift naturally.
 *   - `emit`  — empty sibling that can still before-load: fire `fromLast`.
 */
type SiblingFromLastOutcome =
  | { readonly kind: "skip" }
  | { readonly kind: "abort" }
  | { readonly kind: "emit"; readonly request: LoadRequest };

/** Decide what to do with one earlier sibling during the step (2c) scan. */
function evaluateSiblingForFromLast(
  ctx: LoadPlannerArgs,
  parent: PaginatedNode,
  siblingId: ID,
): SiblingFromLastOutcome {
  if (ctx.cleanedCollapsed.has(siblingId)) {
    return { kind: "skip" };
  }
  const sibling = parent.getChild(siblingId);
  if (!sibling) {
    return { kind: "skip" };
  }
  if (!sibling.pages.isEmpty) {
    // Already has pages — walkNode will drift into it naturally.
    return { kind: "abort" };
  }
  if (!sibling.pages.canLoad("before")) {
    // Before-exhausted empty: skip to avoid re-firing the same fromLast.
    return { kind: "skip" };
  }
  return {
    kind: "emit",
    request: makeLoadRequest({
      ctx,
      node: sibling,
      direction: "before",
      key: { kind: "fromLast" },
    }),
  };
}

/**
 * Build a `LoadRequest` for `node` against `direction`/`key`. Centralises
 * `pageSize` resolution via the planner's `getFetcher`.
 */
function makeLoadRequest(args: MakeLoadRequestArgs): LoadRequest {
  const { ctx, node, direction, key, reload } = args;
  const request: LoadRequest = {
    nodeId: node.id,
    depth: node.depth,
    direction,
    key,
    pageSize: ctx.getFetcher(node.depth).pageSize,
  };
  if (reload) {
    request.reload = true;
  }
  return request;
}
